// =============================================================================
// The resumable upload engine
// =============================================================================
//
// =============================================================================
// WHY THE PART PUTS ARE NOT BUILT ON `host.http`
// =============================================================================
//
// A JSON transport is `fetch`-based, and **`fetch` cannot report upload
// progress**. There is no `upload.onprogress` equivalent in the Fetch API: a
// `fetch()` with a 5 GB body resolves once, at the end, and reports nothing in
// between. `ReadableStream` request bodies (the only workaround) are
// HTTP/2-only, unsupported in Safari, and require `duplex: 'half'`.
//
// A recording that takes twenty minutes to upload on a phone with no progress
// bar is indistinguishable from a hung app, so the part PUTs in this file use
// `XMLHttpRequest` (the one transport in every browser that emits byte-level
// upload progress) unless the host supplies its own `upload.putPart`. The
// CONTROL PLANE (init, presign, list parts, complete, abort) goes through
// `host.http`, so it inherits the host's bearer token, its one-shot
// 401 -> refresh -> retry and its error hooks like every other call.
//
// =============================================================================
// THE PART PUTS ARE CROSS-ORIGIN AND MUST STAY THAT WAY
// =============================================================================
//
// Every part goes DIRECTLY to a presigned S3 URL. The bytes never touch the
// API: the same data-plane rule the worker-node design follows
// (`docs/specs/worker-nodes.md`), and what makes a multi-gigabyte upload from a
// phone survivable at all.
//
// Two consequences are load-bearing:
//
//   1. **The client never reads the `ETag` response header.** A cross-origin
//      response exposes no headers unless the bucket's CORS policy lists them
//      in `ExposeHeaders`, and one misconfigured bucket would then break every
//      upload with an error that looks like a client bug. The platform's
//      `POST :id/upload/complete` requires a `parts` array with an ETag per
//      part, so `completeUpload` below reads the part list back from
//      `GET :id/upload/parts` (an S3 `ListParts` on the server) and forwards it
//      verbatim. That list is authoritative anyway: it describes what the
//      provider actually holds, not what this client believes it sent.
//   2. **The service worker must never see these requests.** A part PUT is
//      `cors`, so it bypasses a navigation-only worker entirely.
//
// =============================================================================
// NO REACT IN THIS FILE
// =============================================================================
//
// Progress is emitted through a `subscribe()` callback, not React state. An
// upload outlives the component that started it, and an engine that called
// `setState` could not be driven from a test without a renderer, could not keep
// running while its screen is unmounted, and would re-render the app on every
// one of the hundreds of progress events a large file produces. The app's
// provider subscribes and throttles into React; this module only ever calls
// listeners.
// =============================================================================
import { KvoxApiError } from '../errors.js';
import { kvoxDelete, kvoxGet, kvoxPost } from '../http.js';
function subscribeToCspViolations(handler) {
    if (typeof document === 'undefined')
        return () => undefined;
    const listener = (event) => {
        const violation = event;
        handler({
            blockedURI: violation.blockedURI ?? '',
            directive: violation.effectiveDirective || violation.violatedDirective || '',
        });
    };
    document.addEventListener('securitypolicyviolation', listener);
    return () => document.removeEventListener('securitypolicyviolation', listener);
}
/**
 * Parallel part uploads on a phone.
 *
 * @stability experimental
 */
export const MOBILE_PART_CONCURRENCY = 3;
/**
 * Parallel part uploads on a desktop.
 *
 * @stability experimental
 */
export const DESKTOP_PART_CONCURRENCY = 4;
/**
 * How many parts to push at once.
 *
 * ⚠️ DETECTED WITH `matchMedia('(pointer: coarse)')`, NOT `navigator.maxTouchPoints`.
 *
 * The question being asked is "is this a phone?", and the two signals answer
 * different questions. `maxTouchPoints > 0` is true of every touchscreen
 * laptop and every Windows convertible in desktop mode — machines with desktop
 * bandwidth, desktop power budgets and no carrier link — so it would hand the
 * conservative mobile setting to hardware that does not need it. A COARSE
 * PRIMARY POINTER means the device's main input is a finger, which is as close
 * as CSS gets to "phone or tablet", and it is also the exact signal the rest
 * of this app already gates its compact treatment on (`useMediaQuery` against
 * MUI breakpoints). Three vs four is a small difference; getting it backwards
 * on a metered phone link is not.
 *
 * Falls back to the desktop value where `matchMedia` is unavailable.
 *
 * @stability experimental
 */
export function detectPartConcurrency() {
    try {
        if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
            return window.matchMedia('(pointer: coarse)').matches
                ? MOBILE_PART_CONCURRENCY
                : DESKTOP_PART_CONCURRENCY;
        }
    }
    catch {
        // A browser that throws on an unknown media feature is still a browser we
        // must upload from. Fall through.
    }
    return DESKTOP_PART_CONCURRENCY;
}
/**
 * The runtime production uses: the real clock, `Math.random`, `XMLHttpRequest` and `navigator`.
 *
 * @stability experimental
 */
export const defaultUploadRuntime = {
    now: () => Date.now(),
    random: () => Math.random(),
    createXhr: () => new XMLHttpRequest(),
    isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
    setTimeout: (handler, ms) => globalThis.setTimeout(handler, ms),
    clearTimeout: (handle) => globalThis.clearTimeout(handle),
    concurrency: () => detectPartConcurrency(),
    onCspViolation: (handler) => subscribeToCspViolations(handler),
};
// =============================================================================
// Tuning constants
// =============================================================================
/**
 * Presigned URLs requested per call.
 *
 * The endpoint accepts at most 100 `partNumbers`; 50 is half that on purpose.
 * A URL has a lifetime, so asking for the maximum up front means the tail of a
 * batch can expire before the engine reaches it on a slow link — every expiry
 * costs a re-presign round trip and a retried PUT. Smaller batches, fetched
 * just ahead of the work, expire far less often, and the extra round trips are
 * one small JSON call per 50 parts.
 *
 * @stability experimental
 */
export const PART_URL_BATCH_SIZE = 50;
/**
 * Treat a URL as dead this long BEFORE its stated expiry.
 *
 * A part PUT that starts one second before expiry and takes thirty seconds on
 * a phone link is a 403 that the engine then has to discover, re-presign and
 * retry — having already sent the bytes. The skew spends a cheap re-presign to
 * avoid re-sending a part.
 *
 * @stability experimental
 */
export const URL_EXPIRY_SKEW_MS = 30_000;
/**
 * Window the speed moving average is computed over.
 *
 * @stability experimental
 */
export const SPEED_WINDOW_MS = 5_000;
/** Cap on retained speed samples, so a long upload cannot grow one unboundedly. */
const MAX_SPEED_SAMPLES = 240;
/**
 * The retry policy of one part.
 *
 * @stability experimental
 */
export const DEFAULT_RETRY_POLICY = {
    maxAttempts: 5,
    baseMs: 500,
    maxMs: 30_000,
};
/**
 * Exponential backoff with **equal jitter**: half the delay is deterministic,
 * half is random within the window.
 *
 * The jitter is not decoration. Parts fail in correlated bursts — a flaky
 * tower, a bucket briefly throttling — and with `concurrency` parts retrying on
 * an identical schedule they would all come back simultaneously, reproduce the
 * same overload, and back off in lockstep again. Spreading the retries is what
 * breaks that convoy.
 *
 * @stability experimental
 */
export function backoffDelayMs(attempt, policy, random) {
    const exponential = Math.min(policy.maxMs, policy.baseMs * 2 ** Math.max(0, attempt - 1));
    return Math.round(exponential / 2 + exponential * 0.5 * random);
}
// =============================================================================
// Control-plane calls
// =============================================================================
/**
 * Start a generic multipart upload through the PLATFORM's init route and adapt
 * its response (`presignedUrls`) to the shape the engine takes (`parts`).
 *
 * @stability experimental
 */
export async function initUpload(host, input) {
    const response = await kvoxPost(host, '/storage/objects/upload/init', input);
    return {
        objectId: response.objectId,
        uploadId: response.uploadId,
        partSize: response.partSize,
        totalParts: response.totalParts,
        parts: response.presignedUrls,
    };
}
/**
 * Ask for more presigned part URLs: the app's `POST /storage/objects/:id/upload/parts`
 * (the platform presigns only the first ten parts at init).
 *
 * ⚠️ The endpoint rejects more than 100 part numbers per call. Callers here
 * never exceed `PART_URL_BATCH_SIZE`; the slice below is belt-and-braces so a
 * future caller cannot turn that into a 400 at runtime.
 *
 * @stability experimental
 */
export async function requestUploadParts(host, objectId, partNumbers) {
    return kvoxPost(host, `/storage/objects/${objectId}/upload/parts`, {
        partNumbers: partNumbers.slice(0, 100),
    });
}
/**
 * The active storage driver cannot list the parts of a multipart upload
 * (`501 list_parts_unsupported`), which this client needs both to resume and to
 * complete without reading an ETag header.
 *
 * @stability experimental
 */
export class ListPartsUnsupportedError extends Error {
    constructor() {
        super('The configured storage provider does not support browser multipart uploads that can be resumed or completed. ' +
            'An administrator needs to switch Storage to an S3-compatible provider.');
        this.name = 'ListPartsUnsupportedError';
    }
}
/**
 * The parts the provider holds for this upload — the app's
 * `GET /storage/objects/:id/upload/parts`, an S3 `ListParts` on the server.
 *
 * Authoritative: it describes what is actually stored, not what this tab
 * believes it sent. Throws `ListPartsUnsupportedError` on a 501, and the
 * ordinary `KvoxApiError` otherwise (404 when the upload no longer exists).
 *
 * @stability experimental
 */
export async function listUploadedParts(host, objectId) {
    try {
        return await kvoxGet(host, `/storage/objects/${objectId}/upload/parts`);
    }
    catch (err) {
        if (err instanceof KvoxApiError && err.status === 501)
            throw new ListPartsUnsupportedError();
        throw err;
    }
}
/**
 * Where a resumed upload stands.
 *
 * Reads the provider's own part list. Only when the driver cannot list parts
 * (501) does it fall back to the platform's `…/upload/status`, which knows only
 * parts recorded at complete time: harmless, since it then reports nothing and
 * every part is re-sent. (Completing such an upload fails with
 * `ListPartsUnsupportedError`, loudly, rather than at the very end of a
 * transfer: the fallback exists so that a 404 still surfaces as "gone".)
 *
 * @stability experimental
 */
export async function fetchUploadResumeState(host, objectId, layout) {
    const totalParts = Math.max(1, Math.ceil(layout.size / layout.partSize));
    let uploadedParts;
    try {
        uploadedParts = (await listUploadedParts(host, objectId)).map((part) => part.partNumber);
    }
    catch (err) {
        if (!(err instanceof ListPartsUnsupportedError))
            throw err;
        const status = await kvoxGet(host, `/storage/objects/${objectId}/upload/status`);
        uploadedParts = status.uploadedParts;
    }
    return {
        partSize: layout.partSize,
        totalParts,
        uploadedParts: uploadedParts.filter((part) => part >= 1 && part <= totalParts),
    };
}
/**
 * Finish the upload.
 *
 * **The client never reads an ETag header.** See the file header: it reads the
 * part list back from the app's `GET …/upload/parts` and forwards it to the
 * platform's `POST …/upload/complete` (`{ parts: [{ partNumber, eTag }] }`), so
 * no upload depends on a bucket CORS `ExposeHeaders` entry for the READ side.
 *
 * Refuses to complete (with `UploadIncompleteError`) when the provider is
 * missing any of `totalParts`: a `CompleteMultipartUpload` over a gap would
 * assemble a silently truncated object.
 *
 * @stability experimental
 */
export async function completeUpload(host, objectId, totalParts) {
    const listed = await listUploadedParts(host, objectId);
    const held = new Map();
    for (const part of listed)
        held.set(part.partNumber, part.etag);
    const missing = [];
    for (let part = 1; part <= totalParts; part += 1) {
        if (!held.has(part))
            missing.push(part);
    }
    if (missing.length > 0)
        throw new UploadIncompleteError(missing);
    const parts = Array.from({ length: totalParts }, (_, index) => ({
        partNumber: index + 1,
        eTag: held.get(index + 1),
    }));
    await kvoxPost(host, `/storage/objects/${objectId}/upload/complete`, { parts });
}
/**
 * Aborts the multipart upload on the server (`DELETE …/upload/abort`).
 *
 * @param host - The host.
 * @param objectId - The storage object of the upload.
 *
 * @stability experimental
 */
export async function abortUpload(host, objectId) {
    await kvoxDelete(host, `/storage/objects/${objectId}/upload/abort`);
}
// =============================================================================
// Errors
// =============================================================================
/**
 * Some parts are not stored at the provider yet, so the upload cannot complete.
 *
 * @stability experimental
 */
export class UploadIncompleteError extends Error {
    /** The 1-based part numbers the provider does not hold. */
    missingParts;
    /**
     * Builds the error.
     *
     * @param missingParts - The part numbers the provider does not hold.
     */
    constructor(missingParts) {
        super(`${missingParts.length} part${missingParts.length === 1 ? '' : 's'} did not reach storage. ` +
            'Resume the upload to send them.');
        this.missingParts = missingParts;
        this.name = 'UploadIncompleteError';
    }
}
/**
 * A part exhausted its attempts, or the control plane refused. Terminal.
 *
 * @stability experimental
 */
export class UploadFailedError extends Error {
    /** The part that failed, when the failure belongs to one. */
    partNumber;
    /** The HTTP status of the last attempt, when there was one. */
    status;
    /**
     * Builds the error.
     *
     * @param message - What to show the user.
     * @param partNumber - The part that failed.
     * @param status - The HTTP status of the last attempt.
     */
    constructor(message, partNumber, status) {
        super(message);
        this.partNumber = partNumber;
        this.status = status;
        this.name = 'UploadFailedError';
    }
}
/**
 * The failure message for a part PUT the browser refused to send because of
 * this site's Content Security Policy.
 *
 * ⚠️ WHY CSP IS SPECIAL-CASED RATHER THAN RETRIED LIKE ANY NETWORK ERROR.
 *
 * A PUT blocked by `connect-src` never leaves the device, and to
 * `XMLHttpRequest` it is indistinguishable from a dropped connection: `onerror`,
 * status 0, no body, no reason. Left to the ordinary retry loop the engine
 * spends every attempt and its backoff on a policy decision that is identical
 * on every try, then reports `Part N failed after N attempts` — which reads as
 * a flaky network and sends the user to find better Wi-Fi, when the only fix
 * is an operator adding the storage origin to the policy (issue #84; the policy allows `https:` by default, so only a plain-http origin needs this). The
 * `securitypolicyviolation` DOM event is the one signal that tells the two
 * apart, so the engine listens for it and fails fast with a message naming
 * the origin and the setting. Every other part targets the same origin under
 * the same policy, so failing the whole upload loses nothing.
 *
 * @stability experimental
 */
export function cspBlockedUploadMessage(origin) {
    return (`Your browser blocked the upload to storage (${origin}) because of this site's ` +
        'Content Security Policy. An administrator needs to allow that origin in infra/nginx/app.d/csp/ (connect-src and media-src).');
}
/**
 * The origin of an absolute URL or bare origin, or `null` for anything that
 * does not parse (`inline`, `eval`, an empty string). Deliberately no base URL:
 * resolving a CSP keyword against `location` would turn it into this page's
 * own origin.
 */
function originOf(value) {
    try {
        const origin = new URL(value).origin;
        return origin === 'null' ? null : origin;
    }
    catch {
        return null;
    }
}
/** Internal: a PUT was aborted by pause/cancel. Never surfaces to callers. */
class PartAbortedError extends Error {
    constructor() {
        super('aborted');
        this.name = 'PartAbortedError';
    }
}
/** Internal: a PUT came back non-2xx. Carries the status so 403 can re-presign. */
class PartHttpError extends Error {
    status;
    constructor(status) {
        super(`part upload failed with HTTP ${status}`);
        this.status = status;
        this.name = 'PartHttpError';
    }
}
// =============================================================================
// The engine
// =============================================================================
class UploadEngine {
    objectId;
    fileName;
    totalBytes;
    host;
    file;
    partSize;
    runtime;
    retry;
    watchNetwork;
    totalParts;
    /** Part numbers still to send, in order. */
    queue = [];
    /** Part numbers S3 has acknowledged. */
    done = new Set();
    /** Bytes reported by `upload.onprogress` for each part currently in flight. */
    inFlightBytes = new Map();
    activeXhrs = new Map();
    /** In-flight parts sent through the host's own `upload.putPart`. */
    activeHostPuts = new Map();
    urls = new Map();
    phase = 'idle';
    error = null;
    waitingForNetwork = false;
    running = 0;
    /**
     * Bumped by every pause/cancel. A callback from a previous generation
     * checks it and returns instead of touching state that has moved on.
     */
    generation = 0;
    listeners = new Set();
    samples = [];
    snapshot;
    settle;
    settled;
    isSettled = false;
    presignChain = Promise.resolve();
    pendingTimers = new Set();
    networkListenersAttached = false;
    onOffline = () => this.handleOffline();
    onOnline = () => this.handleOnline();
    /** Origins this upload has sent (or tried to send) a part PUT to. */
    partOrigins = new Set();
    /** Part origins a `connect-src` violation has been reported for. Never cleared. */
    cspBlockedOrigins = new Set();
    cspUnsubscribe = null;
    constructor(config, options = {}) {
        this.host = config.host;
        this.file = config.file;
        this.objectId = config.objectId;
        this.fileName = config.file.name;
        this.totalBytes = config.file.size;
        this.partSize = config.partSize;
        this.totalParts = config.totalParts;
        this.runtime = { ...defaultUploadRuntime, ...options.runtime };
        this.retry = { ...DEFAULT_RETRY_POLICY, ...options.retry };
        this.watchNetwork = options.watchNetwork !== false;
        for (const part of options.completedParts ?? []) {
            this.done.add(part);
        }
        for (const part of options.presignedParts ?? []) {
            this.urls.set(part.partNumber, part);
        }
        this.queue = [];
        for (let part = 1; part <= this.totalParts; part += 1) {
            if (!this.done.has(part))
                this.queue.push(part);
        }
        this.settled = new Promise((resolve) => {
            this.settle = resolve;
        });
        this.snapshot = this.buildSnapshot();
    }
    // ---------------------------------------------------------------------------
    // Public surface
    // ---------------------------------------------------------------------------
    getProgress() {
        return this.snapshot;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        listener(this.snapshot);
        return () => {
            this.listeners.delete(listener);
        };
    }
    start() {
        if (this.phase !== 'idle' && this.phase !== 'paused')
            return;
        this.attachNetworkListeners();
        if (this.watchNetwork && !this.runtime.isOnline()) {
            // Starting while already offline is not an error; it is the ordinary
            // "picked a file in a lift" case. Park in the same waiting state the
            // `offline` event produces, so `online` resumes it through one path.
            this.phase = 'paused';
            this.waitingForNetwork = true;
            this.emit();
            return;
        }
        this.waitingForNetwork = false;
        this.phase = 'uploading';
        this.error = null;
        this.attachCspListener();
        this.emit();
        this.pump();
    }
    pause() {
        if (this.phase !== 'uploading' && this.phase !== 'idle')
            return;
        this.waitingForNetwork = false;
        this.stopInFlight('paused');
        this.emit();
    }
    resume() {
        if (this.phase !== 'paused')
            return;
        this.start();
    }
    async cancel() {
        if (this.isTerminal())
            return;
        this.stopInFlight('cancelled');
        this.detachNetworkListeners();
        this.emit();
        try {
            await abortUpload(this.host, this.objectId);
        }
        catch {
            // A failed abort leaves an orphaned multipart upload that the bucket's
            // own lifecycle rule reaps. Never surface it: the user asked to stop,
            // and the upload HAS stopped — an error toast here would describe a
            // server-side tidy-up they cannot act on.
        }
        this.finish('cancelled', null);
    }
    whenSettled() {
        return this.settled;
    }
    // ---------------------------------------------------------------------------
    // Scheduling
    // ---------------------------------------------------------------------------
    pump() {
        if (this.phase !== 'uploading')
            return;
        if (this.queue.length === 0 && this.running === 0) {
            void this.complete();
            return;
        }
        const limit = Math.max(1, this.runtime.concurrency());
        while (this.phase === 'uploading' && this.running < limit && this.queue.length > 0) {
            const partNumber = this.queue.shift();
            this.running += 1;
            const generation = this.generation;
            void this.uploadPart(partNumber, generation)
                .then(() => {
                if (generation !== this.generation)
                    return;
                this.done.add(partNumber);
                this.inFlightBytes.delete(partNumber);
                this.emit();
            })
                .catch((err) => {
                if (generation !== this.generation)
                    return;
                this.inFlightBytes.delete(partNumber);
                if (err instanceof PartAbortedError) {
                    // Pause/cancel already moved the phase. Put the part back so a
                    // resume re-sends it: S3 has no partial-part concept, so the
                    // bytes already pushed for it are simply lost.
                    this.requeue(partNumber);
                    return;
                }
                this.fail(err instanceof Error ? err.message : `Part ${partNumber} failed`);
            })
                .finally(() => {
                if (generation !== this.generation)
                    return;
                this.running -= 1;
                this.pump();
            });
        }
    }
    requeue(partNumber) {
        if (this.done.has(partNumber) || this.queue.includes(partNumber))
            return;
        this.queue.push(partNumber);
        this.queue.sort((a, b) => a - b);
    }
    async complete() {
        if (this.phase !== 'uploading')
            return;
        this.phase = 'completing';
        this.emit();
        try {
            await completeUpload(this.host, this.objectId, this.totalParts);
        }
        catch (err) {
            this.fail(err instanceof KvoxApiError ||
                err instanceof ListPartsUnsupportedError ||
                err instanceof UploadIncompleteError
                ? err.message
                : 'Could not finalise the upload');
            return;
        }
        this.detachNetworkListeners();
        this.finish('completed', null);
    }
    // ---------------------------------------------------------------------------
    // One part, with retries
    // ---------------------------------------------------------------------------
    async uploadPart(partNumber, generation) {
        let forceRepresign = false;
        let lastStatus;
        for (let attempt = 1; attempt <= this.retry.maxAttempts; attempt += 1) {
            if (generation !== this.generation || this.phase !== 'uploading') {
                throw new PartAbortedError();
            }
            let url;
            try {
                url = await this.ensureUrl(partNumber, forceRepresign);
            }
            catch (err) {
                throw new UploadFailedError(err instanceof KvoxApiError
                    ? err.message
                    : `Could not get an upload URL for part ${partNumber}`, partNumber);
            }
            if (generation !== this.generation || this.phase !== 'uploading') {
                throw new PartAbortedError();
            }
            try {
                await this.putPart(partNumber, url, generation);
                return;
            }
            catch (err) {
                if (err instanceof PartAbortedError)
                    throw err;
                // A 403 from S3 is what an EXPIRED presigned URL looks like — the
                // signature no longer validates. It is indistinguishable from a policy
                // denial at this layer, and re-presigning costs one small JSON call,
                // so the engine always treats it as expiry and asks for a fresh URL.
                // (A genuine policy denial then fails again and exhausts the attempts,
                // which is the correct outcome either way.)
                forceRepresign = err instanceof PartHttpError && err.status === 403;
                lastStatus = err instanceof PartHttpError ? err.status : undefined;
                this.inFlightBytes.set(partNumber, 0);
                this.emit();
                // A status-0 failure may be a CSP block (see `cspBlockedUploadMessage`).
                // The violation event can be dispatched before OR after `onerror`, so
                // this is checked twice: now, and again after the backoff below, by
                // which time the event has certainly arrived.
                const isNetworkError = !(err instanceof PartHttpError);
                if (isNetworkError)
                    this.throwIfCspBlocked(partNumber, url);
                if (attempt === this.retry.maxAttempts) {
                    throw new UploadFailedError(`Part ${partNumber} failed after ${attempt} attempts`, partNumber, lastStatus);
                }
                await this.delay(backoffDelayMs(attempt, this.retry, this.runtime.random()));
                if (isNetworkError && generation === this.generation) {
                    this.throwIfCspBlocked(partNumber, url);
                }
            }
        }
    }
    /**
     * Throw a terminal `UploadFailedError` — no further attempts, no re-presign —
     * when a `connect-src` violation has been recorded for this part's origin.
     */
    throwIfCspBlocked(partNumber, url) {
        const origin = originOf(url);
        if (origin === null || !this.cspBlockedOrigins.has(origin))
            return;
        throw new UploadFailedError(cspBlockedUploadMessage(origin), partNumber);
    }
    /** The one place `XMLHttpRequest` is used. See the file header for why. */
    putPart(partNumber, url, generation) {
        const hostUpload = this.host.upload;
        if (hostUpload)
            return this.putPartThroughHost(hostUpload, partNumber, url, generation);
        return new Promise((resolve, reject) => {
            const blob = this.slicePart(partNumber);
            const xhr = this.runtime.createXhr();
            this.activeXhrs.set(partNumber, xhr);
            this.inFlightBytes.set(partNumber, 0);
            const cleanup = () => {
                if (this.activeXhrs.get(partNumber) === xhr) {
                    this.activeXhrs.delete(partNumber);
                }
            };
            xhr.upload.onprogress = (event) => {
                if (generation !== this.generation)
                    return;
                this.inFlightBytes.set(partNumber, Math.min(event.loaded, blob.size));
                this.emit();
            };
            xhr.onload = () => {
                cleanup();
                if (xhr.status >= 200 && xhr.status < 300) {
                    // Count the WHOLE part: `upload.onprogress` reports bytes handed to
                    // the socket, which can lag the final acknowledgement by a chunk.
                    this.inFlightBytes.set(partNumber, blob.size);
                    resolve();
                    return;
                }
                reject(new PartHttpError(xhr.status));
            };
            xhr.onerror = () => {
                cleanup();
                reject(new Error(`Network error uploading part ${partNumber}`));
            };
            xhr.ontimeout = () => {
                cleanup();
                reject(new Error(`Timed out uploading part ${partNumber}`));
            };
            xhr.onabort = () => {
                cleanup();
                reject(new PartAbortedError());
            };
            // Recorded BEFORE the send, so a violation dispatched for this request
            // always finds its origin already known.
            const origin = originOf(url);
            if (origin !== null)
                this.partOrigins.add(origin);
            xhr.open('PUT', url, true);
            // NO `Authorization` HEADER, and no `withCredentials`. The presigned URL
            // carries its own signature; adding either would make the browser send a
            // preflight the bucket is unlikely to answer, and S3 rejects a request
            // that is signed two ways.
            xhr.send(blob);
        });
    }
    /**
     * The same part, sent by the host's own transport. Progress moves per part
     * (the port reports no bytes), and an abort rejects like an XHR abort does.
     */
    async putPartThroughHost(port, partNumber, url, generation) {
        const blob = this.slicePart(partNumber);
        const controller = new AbortController();
        this.activeHostPuts.set(partNumber, controller);
        this.inFlightBytes.set(partNumber, 0);
        const origin = originOf(url);
        if (origin !== null)
            this.partOrigins.add(origin);
        try {
            await port.putPart(url, blob, controller.signal);
        }
        catch (err) {
            if (controller.signal.aborted || generation !== this.generation)
                throw new PartAbortedError();
            const status = err?.status;
            if (typeof status === 'number')
                throw new PartHttpError(status);
            throw err instanceof Error ? err : new Error(`Network error uploading part ${partNumber}`);
        }
        finally {
            if (this.activeHostPuts.get(partNumber) === controller)
                this.activeHostPuts.delete(partNumber);
        }
        this.inFlightBytes.set(partNumber, blob.size);
    }
    slicePart(partNumber) {
        const start = (partNumber - 1) * this.partSize;
        const end = Math.min(start + this.partSize, this.totalBytes);
        return this.file.slice(start, end);
    }
    partBytes(partNumber) {
        const start = (partNumber - 1) * this.partSize;
        return Math.max(0, Math.min(start + this.partSize, this.totalBytes) - start);
    }
    // ---------------------------------------------------------------------------
    // Presigned URLs
    // ---------------------------------------------------------------------------
    hasFreshUrl(partNumber) {
        const cached = this.urls.get(partNumber);
        if (!cached)
            return false;
        // An ABSENT expiry (the init endpoint sends none — see `PresignedPart`)
        // and an unparseable one are the same case: nothing is known, so the URL
        // is treated as fresh and a dead one is re-signed on the retry.
        if (cached.expiresAt === undefined)
            return true;
        const expiry = Date.parse(cached.expiresAt);
        if (Number.isNaN(expiry))
            return true;
        return expiry - URL_EXPIRY_SKEW_MS > this.runtime.now();
    }
    async ensureUrl(partNumber, force) {
        if (force)
            this.urls.delete(partNumber);
        if (!this.hasFreshUrl(partNumber)) {
            await this.fetchUrlBatch(partNumber);
        }
        const part = this.urls.get(partNumber);
        if (!part) {
            throw new UploadFailedError(`No upload URL for part ${partNumber}`, partNumber);
        }
        return part.url;
    }
    /**
     * Fetch URLs in batches, SERIALISED through one chain.
     *
     * With `concurrency` parts starting at once, every one of them would
     * otherwise discover the cache empty simultaneously and fire its own
     * presign call for overlapping ranges. Chaining means the first call covers
     * the next `PART_URL_BATCH_SIZE` parts and the rest find their URL already
     * cached — one round trip per 50 parts instead of one per part.
     */
    fetchUrlBatch(partNumber) {
        const run = this.presignChain.then(async () => {
            if (this.hasFreshUrl(partNumber))
                return;
            const wanted = this.collectPartsNeedingUrls(partNumber);
            const parts = await requestUploadParts(this.host, this.objectId, wanted);
            for (const part of parts) {
                this.urls.set(part.partNumber, part);
            }
        });
        this.presignChain = run.then(() => undefined, () => undefined);
        return run;
    }
    collectPartsNeedingUrls(partNumber) {
        const wanted = [partNumber];
        for (let part = partNumber + 1; part <= this.totalParts; part += 1) {
            if (wanted.length >= PART_URL_BATCH_SIZE)
                break;
            if (this.done.has(part))
                continue;
            if (this.hasFreshUrl(part))
                continue;
            wanted.push(part);
        }
        return wanted;
    }
    // ---------------------------------------------------------------------------
    // Network awareness
    // ---------------------------------------------------------------------------
    attachNetworkListeners() {
        if (!this.watchNetwork || this.networkListenersAttached)
            return;
        if (typeof window === 'undefined')
            return;
        window.addEventListener('offline', this.onOffline);
        window.addEventListener('online', this.onOnline);
        this.networkListenersAttached = true;
    }
    detachNetworkListeners() {
        if (!this.networkListenersAttached || typeof window === 'undefined')
            return;
        window.removeEventListener('offline', this.onOffline);
        window.removeEventListener('online', this.onOnline);
        this.networkListenersAttached = false;
    }
    // ---------------------------------------------------------------------------
    // Content Security Policy — see `cspBlockedUploadMessage`
    // ---------------------------------------------------------------------------
    /** Idempotent. Attached only while parts can be in flight. */
    attachCspListener() {
        if (this.cspUnsubscribe)
            return;
        this.cspUnsubscribe = this.runtime.onCspViolation((violation) => this.handleCspViolation(violation));
    }
    detachCspListener() {
        const unsubscribe = this.cspUnsubscribe;
        this.cspUnsubscribe = null;
        unsubscribe?.();
    }
    handleCspViolation(violation) {
        // Older browsers may report the directive with its value attached.
        if (violation.directive.trim().split(/\s+/)[0] !== 'connect-src')
            return;
        // `blockedURI` is a full URL in some browsers and a bare origin in others;
        // comparing origins accepts both. A violation for an origin this upload
        // never targeted belongs to something else on the page.
        const origin = originOf(violation.blockedURI);
        if (origin === null || !this.partOrigins.has(origin))
            return;
        this.cspBlockedOrigins.add(origin);
    }
    handleOffline() {
        if (this.phase !== 'uploading')
            return;
        this.stopInFlight('paused');
        this.waitingForNetwork = true;
        this.emit();
    }
    handleOnline() {
        if (!this.waitingForNetwork)
            return;
        this.waitingForNetwork = false;
        if (this.phase === 'paused') {
            this.start();
        }
        else {
            this.emit();
        }
    }
    // ---------------------------------------------------------------------------
    // State transitions
    // ---------------------------------------------------------------------------
    /** Abort every in-flight PUT and move to `phase`, invalidating callbacks. */
    stopInFlight(phase) {
        this.generation += 1;
        this.phase = phase;
        this.running = 0;
        // Pause, offline, cancel and fail all pass through here; `start()`
        // re-attaches on resume.
        this.detachCspListener();
        for (const [partNumber, xhr] of this.activeXhrs) {
            this.inFlightBytes.delete(partNumber);
            this.requeue(partNumber);
            try {
                xhr.abort();
            }
            catch {
                // An XHR that is already done throws nothing useful; ignore.
            }
        }
        this.activeXhrs.clear();
        for (const [partNumber, controller] of this.activeHostPuts) {
            this.inFlightBytes.delete(partNumber);
            this.requeue(partNumber);
            controller.abort();
        }
        this.activeHostPuts.clear();
        for (const timer of this.pendingTimers) {
            this.runtime.clearTimeout(timer);
        }
        this.pendingTimers.clear();
    }
    fail(message) {
        if (this.isTerminal())
            return;
        this.stopInFlight('failed');
        this.detachNetworkListeners();
        this.error = message;
        this.finish('failed', message);
    }
    finish(status, error) {
        this.phase = status === 'completed' ? 'completed' : status === 'cancelled' ? 'cancelled' : 'failed';
        this.error = error;
        // Covers `complete()`, the one terminal path that skips `stopInFlight`.
        this.detachCspListener();
        if (status === 'completed') {
            // Credit every part: a resumed upload never sent the parts S3 already
            // held, so summing observed bytes would finish a resumed 2 GB file at
            // "400 MB of 2 GB" — correct arithmetic, wrong answer.
            for (let part = 1; part <= this.totalParts; part += 1)
                this.done.add(part);
        }
        this.emit();
        if (this.isSettled)
            return;
        this.isSettled = true;
        this.settle({ objectId: this.objectId, status, error });
    }
    isTerminal() {
        return this.phase === 'completed' || this.phase === 'cancelled' || this.phase === 'failed';
    }
    delay(ms) {
        return new Promise((resolve) => {
            const handle = this.runtime.setTimeout(() => {
                this.pendingTimers.delete(handle);
                resolve();
            }, ms);
            this.pendingTimers.add(handle);
        });
    }
    // ---------------------------------------------------------------------------
    // Progress
    // ---------------------------------------------------------------------------
    uploadedBytes() {
        let total = 0;
        for (const part of this.done)
            total += this.partBytes(part);
        for (const [part, bytes] of this.inFlightBytes) {
            if (this.done.has(part))
                continue;
            total += bytes;
        }
        return Math.min(total, this.totalBytes);
    }
    recordSample(bytes) {
        const t = this.runtime.now();
        this.samples.push({ t, bytes });
        while (this.samples.length > 1 && t - this.samples[0].t > SPEED_WINDOW_MS) {
            this.samples.shift();
        }
        while (this.samples.length > MAX_SPEED_SAMPLES)
            this.samples.shift();
    }
    /**
     * Moving average over `SPEED_WINDOW_MS`, not an all-time average.
     *
     * An all-time average on a 40-minute upload keeps reporting the speed of the
     * first minute long after the user walked out of Wi-Fi range, so the ETA it
     * feeds stays confidently wrong. A short window tracks the link the user is
     * actually on.
     */
    speed() {
        const first = this.samples[0];
        const last = this.samples[this.samples.length - 1];
        if (!first || !last || last.t <= first.t)
            return 0;
        const bytes = last.bytes - first.bytes;
        if (bytes <= 0)
            return 0;
        return (bytes * 1000) / (last.t - first.t);
    }
    buildSnapshot() {
        const uploadedBytes = this.uploadedBytes();
        const bytesPerSecond = this.speed();
        const remaining = Math.max(0, this.totalBytes - uploadedBytes);
        return {
            phase: this.phase,
            uploadedBytes,
            totalBytes: this.totalBytes,
            percent: this.totalBytes === 0
                ? this.phase === 'completed'
                    ? 100
                    : 0
                : Math.min(100, Math.round((uploadedBytes / this.totalBytes) * 1000) / 10),
            completedParts: this.done.size,
            totalParts: this.totalParts,
            bytesPerSecond,
            etaSeconds: bytesPerSecond > 0 ? Math.round(remaining / bytesPerSecond) : null,
            error: this.error,
            waitingForNetwork: this.waitingForNetwork,
        };
    }
    emit() {
        this.recordSample(this.uploadedBytes());
        this.snapshot = this.buildSnapshot();
        for (const listener of this.listeners) {
            try {
                listener(this.snapshot);
            }
            catch {
                // A subscriber that throws must not take the upload down with it.
            }
        }
    }
}
// =============================================================================
// Factories
// =============================================================================
/**
 * Start an upload from an init response already in hand.
 *
 * Separate from `createTranscriptUpload` so the init call and the transfer can
 * be tested — and retried — independently, and so a caller that obtained an
 * init response some other way (a server-rendered hand-off, a test fixture)
 * does not have to fake an endpoint to use the engine.
 *
 * @stability experimental
 */
export function startUpload(host, file, init, options = {}) {
    const upload = new UploadEngine({
        host,
        file,
        objectId: init.objectId,
        partSize: init.partSize,
        totalParts: init.totalParts,
    }, { ...options, presignedParts: init.parts ?? options.presignedParts });
    upload.start();
    return upload;
}
/**
 * Init + start through the PLATFORM's generic init route, for a file that is
 * not tied to a transcript row. (A transcript's own upload is initialised by
 * `POST /api/transcripts` and adopted through `startUpload`; the platform's
 * init takes no transcript id, so none is accepted here.)
 *
 * @stability experimental
 */
export async function createTranscriptUpload(host, file, engineOptions = {}) {
    const init = await initUpload(host, {
        name: file.name,
        size: file.size,
        mimeType: file.type || 'application/octet-stream',
    });
    return { upload: startUpload(host, file, init, engineOptions), init };
}
/**
 * Resume an upload the server already knows about.
 *
 * The resume state (`fetchUploadResumeState`, the provider's own part list) is
 * the ONLY authority on what has landed — never a local record of what this tab
 * believed it sent. A tab that died mid-PUT, another
 * device that uploaded some of the same object, a part S3 rejected after the
 * browser closed: all of them make a local tally wrong in the direction that
 * silently corrupts the object (skipping a part that was never stored).
 *
 * @stability experimental
 */
export function resumeUpload(host, file, objectId, state, options = {}) {
    const upload = new UploadEngine({
        host,
        file,
        objectId,
        partSize: state.partSize,
        totalParts: state.totalParts,
    }, {
        ...options,
        completedParts: state.uploadedParts,
    });
    upload.start();
    return upload;
}
/**
 * Builds an upload engine WITHOUT starting it. Call `start()` when ready.
 *
 * @remarks
 * The lower-level form of {@link startUpload} and {@link resumeUpload}, for a
 * caller that wants to subscribe before the first byte moves. Seed
 * `completedParts` to resume and `presignedParts` with the init response.
 *
 * @param host - The host whose `http` carries the control plane.
 * @param init - The file, its object and part layout, and the engine options.
 * @returns The upload handle, in the `idle` phase.
 *
 * @stability experimental
 */
export function createResumableUpload(host, init) {
    const { file, objectId, partSize, totalParts, ...options } = init;
    return new UploadEngine({ host, file, objectId, partSize, totalParts }, options);
}
